import { ZuruflexApiError } from '@zuruflex/api-client';
import { formatDateTimeIn, formatMoneyIn, toLocale } from '@zuruflex/i18n';
import type { Booking } from '@zuruflex/types';
import { formatPhone } from '@zuruflex/utils';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Money,
  PageHeader,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@zuruflex/ui';
import { LifeBuoy, MessageSquare } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DeclineBookingDialog } from '../components/DeclineBookingDialog.js';
import { GuestReviewDialog } from '../components/GuestReviewDialog.js';
import { HandoverDialog } from '../components/HandoverDialog.js';
import { ReturnDialog } from '../components/ReturnDialog.js';
import { api } from '../lib/api.js';
import { useSupport } from '../lib/support.js';
import { promoLabel, settlementOf } from '../lib/promotions.js';

/**
 * The list endpoint expands the car and the customer. Both matter here:
 * a partner with two cars cannot run a handover from a reference and a
 * date alone — they need to know which car, and who is collecting it.
 */
interface BookingRow extends Booking {
  vehicle?: { make: string; model: string; plateNumber: string } | null;
  customer?: { fullName: string | null; phone: string } | null;
}

const STATUS_VARIANT = {
  PENDING: 'warning',
  AWAITING_PAYMENT: 'info',
  CONFIRMED: 'info',
  ACTIVE: 'success',
  COMPLETED: 'default',
  CANCELLED: 'outline',
} as const;

const TABS = ['needs', 'upcoming', 'past'] as const;
type Tab = (typeof TABS)[number];

function isTab(value: string | null): value is Tab {
  return value !== null && (TABS as readonly string[]).includes(value);
}

/**
 * Which of the three lists a hire belongs in.
 *
 * The split is by what the partner has to do about it, not by status: a
 * confirmed hire is something to look forward to until its pick-up time
 * arrives, at which point it is a handover somebody is standing there
 * waiting for. Same status, different list.
 */
function tabFor(booking: BookingRow, now: number, owesReview: boolean): Tab {
  switch (booking.status) {
    case 'PENDING':
    case 'ACTIVE':
      return 'needs';
    case 'CONFIRMED':
      return new Date(booking.pickupAt).getTime() <= now ? 'needs' : 'upcoming';
    case 'AWAITING_PAYMENT':
      return 'upcoming';
    case 'COMPLETED':
      return owesReview ? 'needs' : 'past';
    default:
      return 'past';
  }
}

/**
 * The one step this booking is waiting on the partner for. Cancelling is
 * absent on purpose — it is a refund conversation, and the API refuses it
 * from a partner token anyway.
 */
function nextStep(status: Booking['status']): { kind: 'PICKUP' | 'RETURN'; key: string } | null {
  switch (status) {
    /*
     * PENDING is not advanced from here any more — it is a reservation
     * waiting on this partner to accept it, which is a decision with two
     * answers rather than a step forward. See the accept/decline pair.
     *
     * AWAITING_PAYMENT is the customer's move, not the partner's: the
     * booking advances itself when the money arrives.
     */
    /*
     * Both open a dialog that records the car before the status moves —
     * the status move is what seals the record, so it is the last thing
     * the dialog does rather than the whole of what this button does.
     */
    case 'CONFIRMED':
      return { kind: 'PICKUP', key: 'bookings.handOver' };
    case 'ACTIVE':
      return { kind: 'RETURN', key: 'bookings.complete' };
    default:
      return null;
  }
}

/** Three of the real thing's height, so the page does not jump when it loads. */
function LoadingRows() {
  return (
    <ul className="flex flex-col gap-3" aria-hidden>
      {[0, 1, 2].map((row) => (
        <li
          key={row}
          className="flex items-center gap-4 rounded-card border border-border-default bg-surface-raised p-4"
        >
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-56" />
            <Skeleton className="h-3 w-72" />
            <Skeleton className="h-3 w-40" />
          </div>
          <Skeleton className="h-8 w-24 shrink-0" />
        </li>
      ))}
    </ul>
  );
}

export function BookingsPage() {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const locale = toLocale(i18n.language);
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  /** The hire whose guest is being reviewed, or null when the dialog is shut. */
  const [reviewing, setReviewing] = useState<{
    reference: string;
    guestName: string;
    vehicle: string;
  } | null>(null);
  /** The reservation being turned down, or null when the dialog is shut. */
  const [declining, setDeclining] = useState<{
    reference: string;
    guestName: string;
    vehicle: string;
    when: string;
  } | null>(null);

  /*
   * Both the tab and the hire to single out live in the URL. That is what
   * makes the overview's rows work: they are links to a booking, and a
   * link has to survive being opened in a new tab or bookmarked.
   */
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { openSupport } = useSupport();
  const focused = params.get('ref');

  const {
    data,
    isPending,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: ['my-bookings'],
    queryFn: () => api.listBookings({ perPage: 100 }),
  });

  /*
   * Which finished hires still owe a guest review, so a completed row can
   * offer the button without each one asking separately.
   */
  const { data: owed } = useQuery({
    queryKey: ['guests-to-review'],
    queryFn: () => api.pendingGuestReviews({ perPage: 100 }),
  });
  const owedRefs = new Set((owed?.items ?? []).map((hire) => hire.reference));

  /*
   * The renter's conversation, pointed at this hire. One thread per renter
   * rather than per booking, so the booking id is what selects the subject
   * rather than the thread.
   */
  const message = useMutation({
    mutationFn: (bookingId: string) => api.openThread({ kind: 'DIRECT', bookingId }),
    onSuccess: (thread) => navigate(`/inbox/${thread.id}`),
  });

  /**
   * The hire whose car is changing hands, and which way, or null when
   * neither dialog is open. The two dialogs do their own writes — the
   * record, the photographs, then the status — so there is no mutation
   * for this on the page any more.
   */
  const [handover, setHandover] = useState<{
    kind: 'PICKUP' | 'RETURN';
    reference: string;
  } | null>(null);

  /**
   * Accepting or turning down a reservation.
   *
   * Separate from `advance` because it is not a step along the state
   * machine — it is the answer to a question, and a decline has to carry
   * a reason the customer will read.
   */
  const decide = useMutation({
    mutationFn: ({
      reference,
      decision,
      reason,
    }: {
      reference: string;
      decision: 'APPROVED' | 'DECLINED';
      reason?: string;
    }) => api.decideBooking(reference, { decision, reason }),
    onMutate: () => setError(null),
    onError: (cause) =>
      setError(cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected')),
    onSuccess: () => {
      setDeclining(null);
      void queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
    },
  });

  const now = Date.now();
  const all = (data?.items ?? []) as BookingRow[];

  /**
   * Chronological by pick-up, not by when the booking was taken. This is
   * an operational list — the question it answers is "what is next", and
   * a hire in two days matters more than one booked more recently for
   * November. Finished hires run the other way: the most recent first,
   * because nobody scrolls to the bottom to find last week.
   */
  const byPickup = (a: BookingRow, b: BookingRow) =>
    new Date(a.pickupAt).getTime() - new Date(b.pickupAt).getTime();

  const grouped: Record<Tab, BookingRow[]> = { needs: [], upcoming: [], past: [] };
  for (const booking of all) {
    grouped[tabFor(booking, now, owedRefs.has(booking.reference))].push(booking);
  }
  grouped.needs.sort(byPickup);
  grouped.upcoming.sort(byPickup);
  grouped.past.sort((a, b) => byPickup(b, a));

  /*
   * An explicit ?tab wins; failing that, the tab holding the hire that was
   * linked to; failing that, whichever list has something in it, because
   * landing on an empty "Needs you" when there are ten upcoming hires
   * reads as a portal with nothing in it.
   */
  const tabWithFocus = focused
    ? TABS.find((tab) => grouped[tab].some((booking) => booking.reference === focused))
    : undefined;
  const fromParam = params.get('tab');
  const tab: Tab = isTab(fromParam)
    ? fromParam
    : (tabWithFocus ?? (grouped.needs.length > 0 ? 'needs' : 'upcoming'));

  const selectTab = (next: string) => {
    const nextParams = new URLSearchParams(params);
    nextParams.set('tab', next);
    // The ring belonged to the row we are navigating away from.
    nextParams.delete('ref');
    setParams(nextParams, { replace: true });
  };

  /*
   * Stable, so it runs when the row mounts rather than on every render —
   * a scroll that repeats itself would fight the partner for the page.
   */
  const revealFocused = useCallback((node: HTMLLIElement | null) => {
    node?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, []);

  const vehicleName = (booking: BookingRow) =>
    booking.vehicle
      ? `${booking.vehicle.make} ${booking.vehicle.model}`
      : t('common:field.vehicle');

  const guestName = (booking: BookingRow) =>
    booking.customer?.fullName ?? t('bookings.unnamedCustomer');

  function renderRow(booking: BookingRow) {
    const step = nextStep(booking.status);
    const { promo } = settlementOf(booking);
    const isFocused = booking.reference === focused;

    return (
      <li
        key={booking.id}
        ref={isFocused ? revealFocused : undefined}
        className={[
          'flex flex-wrap items-center gap-4 rounded-card border bg-surface-raised p-4',
          isFocused
            ? 'border-accent ring-2 ring-accent ring-offset-2 ring-offset-surface'
            : 'border-border-default',
        ].join(' ')}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-text">{vehicleName(booking)}</span>
            {booking.vehicle ? (
              <span className="font-mono text-xs text-text-muted">
                {booking.vehicle.plateNumber}
              </span>
            ) : null}
            <Badge variant={STATUS_VARIANT[booking.status]}>
              {t(`common:bookingStatus.${booking.status}`)}
            </Badge>
          </div>

          <p className="mt-1 text-sm">
            {formatDateTimeIn(booking.pickupAt, locale)} →{' '}
            {formatDateTimeIn(booking.dropoffAt, locale)}
          </p>
          {booking.deliveryAddress ? (
            <p className="mt-0.5 text-sm">
              {t('partner:bookings.deliverTo', { where: booking.deliveryAddress })}
            </p>
          ) : null}

          <p className="mt-0.5 text-xs text-text-muted">
            <span className="font-mono">{booking.reference}</span>
            {booking.customer ? (
              <>
                {' · '}
                {guestName(booking)}
                {/*
                  The number only arrives from CONFIRMED — the API withholds
                  it before that, and there is nothing here to render. Until
                  then the Message button below is the way to reach them,
                  which is the point: a hire that has not been agreed or paid
                  for is exactly the one that gets taken off the platform.
                */}
                {booking.customer.phone ? (
                  <>
                    {' · '}
                    <a
                      href={`tel:${booking.customer.phone}`}
                      className="underline underline-offset-2"
                    >
                      {formatPhone(booking.customer.phone)}
                    </a>
                  </>
                ) : null}
              </>
            ) : null}
          </p>
        </div>

        {/* Fixed width, so the amounts line up down the list
            instead of drifting with the length of each car's name. */}
        <div className="w-32 shrink-0 text-right">
          <p className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
            {t('bookings.yourShare')}
          </p>
          {/* What they are actually paid, not the headline total —
              the difference is Zuruflex's commission and showing the
              bigger number would be a promise we do not keep. */}
          <Money amount={booking.partnerEarnings ?? 0} currency={booking.currency} />
          {/*
            A promo on the hire, and whose money it was. The share above is
            already net of a promo this partner runs, and untouched by one
            Zuruflex runs — without this line the two look like a rate that
            changes from one hire to the next.
          */}
          {promo ? (
            <p className="mt-0.5 text-[11px] leading-tight text-text-muted">
              {promoLabel(promo.code, t)}
              <br />
              {t(promo.funder === 'PARTNER' ? 'bookings.promoYours' : 'bookings.promoZuruflex', {
                amount: formatMoneyIn(promo.amount, booking.currency, locale),
              })}
            </p>
          ) : null}
        </div>

        <div className="w-28 shrink-0 text-right">
          {booking.status === 'PENDING' ? (
            <div className="flex flex-col gap-1">
              <Button
                size="sm"
                isLoading={decide.isPending && decide.variables?.reference === booking.reference}
                onClick={() =>
                  decide.mutate({ reference: booking.reference, decision: 'APPROVED' })
                }
              >
                {t('bookings.accept')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={decide.isPending}
                onClick={() =>
                  setDeclining({
                    reference: booking.reference,
                    guestName: guestName(booking),
                    vehicle: vehicleName(booking),
                    when: formatDateTimeIn(booking.pickupAt, locale),
                  })
                }
              >
                {t('bookings.decline')}
              </Button>
            </div>
          ) : step ? (
            <Button
              size="sm"
              onClick={() => setHandover({ kind: step.kind, reference: booking.reference })}
            >
              {t(step.key)}
            </Button>
          ) : owedRefs.has(booking.reference) ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setReviewing({
                  reference: booking.reference,
                  guestName: booking.customer?.fullName ?? t('common:field.customer'),
                  vehicle: vehicleName(booking),
                })
              }
            >
              {t('reviews.reviewGuest')}
            </Button>
          ) : booking.status === 'COMPLETED' ? (
            /* Written, and waiting on the guest — visible proof the
               double-blind is doing something, rather than the row
               just going quiet. */
            <span className="text-xs text-text-muted">{t('reviews.awaitingReveal')}</span>
          ) : null}

          {/*
            Always available, under whatever the row's decision is. An owner
            with a question about a reservation they are about to decline is
            the case this exists for, and burying it behind Accept would mean
            the only way to ask was to say yes first.
          */}
          {/*
            A call rather than a link, because there is no longer a thread
            per booking to link to: one conversation carries every hire this
            renter has taken, and opening it from here is what points its
            head at this one. Nothing is written — the composer is left
            empty for whatever the owner actually wants to ask.
          */}
          <div className="mt-1 flex flex-wrap gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="flex-1 justify-center px-2 text-xs"
              disabled={message.isPending}
              onClick={() => message.mutate(booking.id)}
            >
              <MessageSquare className="size-3.5" />
              {t('inbox.messageRenter')}
            </Button>

            {/*
              The other half of the same question. Some of what an owner
              wants to ask about a hire is not for the renter — a payment
              that has not landed, a guest who is not answering — and the
              row is where they are when they think of it. It opens the
              same dialog the inbox does, with this booking already on it,
              so nobody has to copy a reference across two pages.
            */}
            <Button
              size="sm"
              variant="ghost"
              className="flex-1 justify-center px-2 text-xs"
              onClick={() =>
                openSupport({
                  bookingId: booking.id,
                  bookingReference: booking.reference,
                  // Named, like every other contextual door — see
                  // `SupportRequestContext.subject`.
                  subject: booking.reference,
                })
              }
            >
              <LifeBuoy className="size-3.5" />
              {t('common:support.entryLabel')}
            </Button>
          </div>
        </div>
      </li>
    );
  }

  return (
    <>
      <PageHeader
        title={t('bookings.title')}
        description={data ? t('bookings.count', { count: data.total }) : t('common:state.loading')}
      />

      {error ? (
        <Alert variant="danger" className="mb-6">
          {error}
        </Alert>
      ) : null}

      {loadError ? (
        <ErrorState error={loadError} onRetry={() => void refetch()} />
      ) : isPending ? (
        <LoadingRows />
      ) : all.length === 0 ? (
        <EmptyState title={t('bookings.none')} description={t('bookings.noneBody')} />
      ) : (
        /*
         * Three lists rather than one. The single list was chronological,
         * which put the reservation waiting on an answer wherever its
         * pick-up date happened to fall — behind six settled hires, as
         * often as not.
         */
        <Tabs value={tab} onValueChange={selectTab}>
          <TabsList>
            {TABS.map((key) => (
              <TabsTrigger key={key} value={key}>
                {t(`bookings.tab.${key}`)}
                {grouped[key].length > 0 ? (
                  <span className="text-xs tabular-nums opacity-60">{grouped[key].length}</span>
                ) : null}
              </TabsTrigger>
            ))}
          </TabsList>

          {TABS.map((key) => (
            <TabsContent key={key} value={key}>
              {grouped[key].length === 0 ? (
                <p className="py-8 text-center text-sm text-text-muted">
                  {t(`bookings.empty.${key}`)}
                </p>
              ) : (
                <ul className="flex flex-col gap-3">{grouped[key].map(renderRow)}</ul>
              )}
            </TabsContent>
          ))}
        </Tabs>
      )}

      {reviewing ? (
        <GuestReviewDialog
          open
          onOpenChange={(next) => {
            if (!next) setReviewing(null);
          }}
          booking={reviewing}
        />
      ) : null}

      {handover?.kind === 'PICKUP' ? (
        <HandoverDialog reference={handover.reference} onClose={() => setHandover(null)} />
      ) : null}

      {handover?.kind === 'RETURN' ? (
        <ReturnDialog reference={handover.reference} onClose={() => setHandover(null)} />
      ) : null}

      {declining ? (
        <DeclineBookingDialog
          open
          onOpenChange={(next) => {
            if (!next) setDeclining(null);
          }}
          booking={declining}
          isPending={decide.isPending}
          error={decide.isError ? error : null}
          onConfirm={(reason) =>
            decide.mutate({ reference: declining.reference, decision: 'DECLINED', reason })
          }
        />
      ) : null}

      {/* Only worth saying once there is something that could be cancelled. */}
      {all.length > 0 ? (
        <p className="mt-6 text-xs text-text-muted">{t('bookings.cancelIsZuruflex')}</p>
      ) : null}
    </>
  );
}
